'use client';

import { useTranslations } from 'next-intl';
import {
  appStoreAvailable,
  googlePlayStore,
  STORE_URLS,
  type StoreApp,
} from '@/lib/storeAvailability';
import { cn } from '@/lib/utils';

type StoreBadgesProps = {
  app: StoreApp;
  className?: string;
};

function StoreBadge({
  available,
  href,
  src,
  alt,
  comingSoon,
}: {
  available: boolean;
  href: string;
  src: string;
  alt: string;
  comingSoon: string;
}) {
  const image = (
    <img src={src} alt={alt} className="h-14 w-[210px] object-contain" loading="lazy" />
  );

  return (
    <div className="flex items-center gap-3">
      {available ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block">
          {image}
        </a>
      ) : (
        <div className="block opacity-75 grayscale pointer-events-none select-none">{image}</div>
      )}
      {!available ? (
        <span className="text-sm font-medium text-foreground/65">{comingSoon}</span>
      ) : null}
    </div>
  );
}

export function StoreBadges({ app, className }: StoreBadgesProps) {
  const t = useTranslations('download');
  const comingSoon = t('comingSoon');
  const urls = STORE_URLS[app];

  return (
    <div className={cn('flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center', className)}>
      <StoreBadge
        available={googlePlayStore}
        href={urls.playStore}
        src="/images/PlayStore_badge.png"
        alt={t(`${app}.playStore`)}
        comingSoon={comingSoon}
      />
      <StoreBadge
        available={appStoreAvailable}
        href={urls.appStore}
        src="/images/AppStore_badge.png"
        alt={t(`${app}.appStore`)}
        comingSoon={comingSoon}
      />
    </div>
  );
}
